/**
 * Import de lieux et d'événements depuis un fichier tabulaire (xlsx, xls, csv…).
 *
 * Transforme un programme d'événements en lieux (points opérationnels) et en
 * créneaux de check-in : un lieu = un point, chaque événement = un créneau.
 *
 * Aucune dépendance React : la logique est testable isolément.
 */
import * as XLSX from 'xlsx';
import { normalizeString } from './textUtils';

export { normalizeString };

/**
 * Alias de colonnes acceptés. Le fichier peut venir de sources variées :
 * on cherche par nom exact puis par inclusion, sans tenir compte des accents.
 */
const COLUMN_ALIASES: Record<string, string> = {
  // Lieu
  lieux: 'venue', lieu: 'venue', venue: 'venue', salle: 'venue', 'nom du lieu': 'venue',
  location: 'venue', place: 'venue', site: 'venue', adresse: 'venue',
  // Événement
  'evenements': 'title', 'événement': 'title', evenement: 'title', titre: 'title',
  title: 'title', event: 'title', 'nom de l evenement': 'title', spectacle: 'title',
  // Description
  description: 'description', resume: 'description', 'résumé': 'description', details: 'description',
  // Catégorie (type d'événement)
  'categories': 'category', 'catégorie': 'category', categorie: 'category',
  category: 'category', type: 'category', genre: 'category',
  // Date
  date: 'date', jour: 'date', 'date de l evenement': 'date', day: 'date',
  // Heures
  'heure de debut': 'start', 'heure de début': 'start', 'debut': 'start', 'début': 'start',
  start: 'start', 'start time': 'start', 'heure debut': 'start', ouverture: 'start', heure: 'start',
  'heure de fin': 'end', fin: 'end', end: 'end', 'end time': 'end', 'heure fin': 'end', fermeture: 'end',
  // Billetterie
  billet: 'ticket', billets: 'ticket', ticket: 'ticket', tarif: 'ticket', prix: 'ticket', price: 'ticket',
  'quantite max billets': 'maxQty', 'quantité max billets': 'maxQty',
  'quantite max': 'maxQty', 'quantité max': 'maxQty', quantite: 'maxQty', 'quantité': 'maxQty',
  places: 'maxQty', capacite: 'maxQty', 'capacité': 'maxQty', quantity: 'maxQty', qty: 'maxQty',
  // Lien
  'lien de l evenement': 'link', 'lien de l événement': 'link', lien: 'link', link: 'link',
  url: 'link', site_web: 'link',
};

/** Mappe les en-têtes du fichier vers les champs connus (2 passes, comme ImportTeam). */
export function findColumnMap(headers: unknown[]): Record<string, number> {
  const colMap: Record<string, number> = {};
  const raw = headers.map(h => (h ?? '').toString().trim());

  // Passe 1 — égalité exacte sur les alias normalisés
  raw.forEach((h, i) => {
    const norm = normalizeString(h);
    if (!norm) return;
    for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
      if (norm === normalizeString(alias) && colMap[field] === undefined) {
        colMap[field] = i;
        break;
      }
    }
  });

  // Passe 2 — inclusion, pour les en-têtes composés (« Heure de début de l'événement »)
  raw.forEach((h, i) => {
    const norm = normalizeString(h);
    if (!norm || Object.values(colMap).includes(i)) return;
    for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
      const normAlias = normalizeString(alias);
      if (normAlias && norm.includes(normAlias) && colMap[field] === undefined) {
        colMap[field] = i;
        break;
      }
    }
  });

  return colMap;
}

// ─────────────────────────────────────────────────────────────
// Dates et heures
// ─────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Numéro de série Excel → `YYYY-MM-DD`.
 * Excel compte les jours depuis le 30/12/1899 (bug du 29/02/1900 inclus).
 */
function excelSerialToDate(serial: number): string | null {
  if (!isFinite(serial) || serial < 1 || serial > 200000) return null;
  const ms = Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000;
  return new Date(ms).toISOString().slice(0, 10);
}

/** Accepte un numéro de série Excel, un objet Date, `YYYY-MM-DD` ou `JJ/MM/AAAA`. */
export function parseDateValue(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  if (typeof v === 'number') return excelSerialToDate(v);

  const s = v.toString().trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  const fr = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (fr) {
    const [, d, m, y] = fr;
    const year = y.length === 2 ? `20${y}` : y;
    return `${year}-${pad(+m)}-${pad(+d)}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  }
  return null;
}

/**
 * Heure → `HH:MM`.
 * Gère la fraction de journée d'Excel (0.8125 = 19h30) et les saisies texte
 * abîmées du type « 18;30 » ou « 18h30 ».
 */
export function parseTimeValue(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${pad(v.getHours())}:${pad(v.getMinutes())}`;
  }
  if (typeof v === 'number') {
    if (!isFinite(v)) return null;
    // Fraction de journée. Une valeur ≥ 1 est un numéro de série daté :
    // on ne garde que la partie décimale (l'heure dans la journée).
    const frac = v >= 1 ? v - Math.floor(v) : v;
    if (frac < 0 || frac >= 1) return null;
    const minutes = Math.round(frac * 1440) % 1440;
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
  }

  const s = v.toString().trim().toLowerCase();
  if (!s) return null;
  // « 18;30 », « 18h30 », « 18.30 », « 18:30 », « 18h »
  const m = s.match(/^(\d{1,2})\s*[h:;.,]\s*(\d{1,2})?/);
  if (m) {
    const h = +m[1];
    const min = m[2] ? +m[2] : 0;
    if (h > 23 || min > 59) return null;
    return `${pad(h)}:${pad(min)}`;
  }
  // « 8 » ou « 8.5 » écrit en texte
  const n = Number(s.replace(',', '.'));
  if (isFinite(n)) return parseTimeValue(n);
  return null;
}

/** Ajoute un jour à une date `YYYY-MM-DD` (créneau qui franchit minuit). */
function nextDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  return dt.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────
// Effectif
// ─────────────────────────────────────────────────────────────

/** Billets par personne — sert à estimer l'effectif d'un créneau. */
const TICKETS_PER_STAFF = 250;

/**
 * Effectif indicatif d'un créneau :
 * - billetterie gratuite → 0
 * - quantité numérique    → 1 personne par 250 billets, minimum 1
 * - « x », vide, inconnu  → 1
 */
export function computeRequiredTeam(ticket: string, maxQty: string, totalTickets: number): number {
  if (/gratuit|free/i.test(ticket)) return 0;
  if (totalTickets > 0) return Math.max(1, Math.round(totalTickets / TICKETS_PER_STAFF));
  return 1;
}

/** Somme les quantités de billets de plusieurs lignes fusionnées. `x` et vides comptent 0. */
function sumTickets(values: string[]): number {
  return values.reduce((sum, v) => {
    const n = Number(v.toString().replace(/[^\d.]/g, ''));
    return sum + (isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

// ─────────────────────────────────────────────────────────────
// Lecture du fichier
// ─────────────────────────────────────────────────────────────

/** Lit la première feuille du fichier et renvoie les lignes brutes. */
export async function readRows(file: File): Promise<unknown[][]> {
  const buffer = await file.arrayBuffer();
  const isCsv = file.name.toLowerCase().endsWith('.csv');

  let workbook;
  if (isCsv) {
    // Décodage UTF-8 explicite : sans cela les accents ressortent abîmés.
    const text = new TextDecoder('utf-8').decode(buffer).replace(/^﻿/, '');
    workbook = XLSX.read(text, { type: 'string', codepage: 65001 });
  } else {
    workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
}

// ─────────────────────────────────────────────────────────────
// Construction des lieux
// ─────────────────────────────────────────────────────────────

export interface ImportedEvent {
  title: string;
  description: string;
  category: string;
  date: string;
  start: string;
  end: string;
  venue: string;
  ticket: string;
  maxQty: string;
  link: string;
}

export interface PlaceSlot {
  /** Créneau au format de l'application : ISO local naïf, ex. `2026-10-23T19:30`. */
  start: string;
  end: string;
  requiredTeam: number;
  /** Événements rattachés à ce créneau (plusieurs si les billets étaient éclatés). */
  events: ImportedEvent[];
}

export interface PlaceDraft {
  /** Nom affiché, tel qu'écrit dans le fichier. */
  name: string;
  /** Nom normalisé, pour retrouver un lieu déjà existant. */
  key: string;
  slots: PlaceSlot[];
  /** Détail des événements, prêt à être écrit dans `description_markdown`. */
  description: string;
}

export interface ParseResult {
  places: PlaceDraft[];
  rowsRead: number;
  rowsSkipped: number;
  /** Anomalies rencontrées, à montrer à l'utilisateur. */
  warnings: string[];
}

const cell = (row: unknown[], idx: number | undefined): string =>
  idx === undefined ? '' : (row[idx] ?? '').toString().trim();

/**
 * Transforme les lignes du fichier en brouillons de lieux.
 *
 * Règles :
 * - un lieu = un point ; les événements du lieu deviennent ses créneaux ;
 * - deux lignes de même titre au même lieu le même jour = le même événement
 *   (billets First/Second Release éclatés) → un seul créneau, horaire le plus large ;
 * - un événement sur deux dates distinctes reste deux créneaux ;
 * - les noms de lieux sont comparés sans accent ni casse (« B7L9 Art centre » ≡ « B7L9 Art Centre »).
 */
export function buildPlaces(rows: unknown[][]): ParseResult {
  const warnings: string[] = [];
  if (rows.length < 2) {
    return { places: [], rowsRead: 0, rowsSkipped: 0, warnings: ['Le fichier ne contient aucune ligne de données.'] };
  }

  const colMap = findColumnMap(rows[0]);
  if (colMap.venue === undefined) {
    warnings.push('Aucune colonne « Lieu » reconnue — impossible de créer les points.');
  }
  if (colMap.date === undefined) {
    warnings.push('Aucune colonne « Date » reconnue.');
  }

  const events: ImportedEvent[] = [];
  let rowsSkipped = 0;

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.every(c => c === '' || c === null || c === undefined)) continue;

    const title = cell(row, colMap.title);
    const venue = cell(row, colMap.venue);
    // Une ligne sans titre ET sans lieu n'est pas exploitable
    if (!title && !venue) { rowsSkipped++; continue; }

    const date = parseDateValue(colMap.date !== undefined ? row[colMap.date] : null);
    if (!date) { rowsSkipped++; warnings.push(`Ligne ${r + 1} : date illisible — ligne ignorée.`); continue; }
    if (!venue) { rowsSkipped++; warnings.push(`Ligne ${r + 1} : lieu manquant — ligne ignorée.`); continue; }

    const start = parseTimeValue(colMap.start !== undefined ? row[colMap.start] : null);
    if (!start) { rowsSkipped++; warnings.push(`Ligne ${r + 1} : heure de début illisible — ligne ignorée.`); continue; }

    let end = parseTimeValue(colMap.end !== undefined ? row[colMap.end] : null);
    if (!end) {
      end = start;
      warnings.push(`Ligne ${r + 1} : heure de fin illisible — fin alignée sur le début.`);
    }

    events.push({
      title: title || '(sans titre)',
      description: cell(row, colMap.description),
      category: cell(row, colMap.category),
      date,
      start,
      end,
      venue,
      ticket: cell(row, colMap.ticket),
      maxQty: cell(row, colMap.maxQty),
      link: cell(row, colMap.link),
    });
  }

  // Regroupement : lieu → (date + titre) → créneau
  const byVenue = new Map<string, { name: string; slots: Map<string, PlaceSlot> }>();

  for (const ev of events) {
    const venueKey = normalizeString(ev.venue);
    if (!byVenue.has(venueKey)) byVenue.set(venueKey, { name: ev.venue, slots: new Map() });

    const venueEntry = byVenue.get(venueKey)!;
    const slotKey = `${ev.date}|${normalizeString(ev.title)}`;

    const existing = venueEntry.slots.get(slotKey);
    if (!existing) {
      // Fin antérieure au début = l'événement franchit minuit
      const endDate = ev.end <= ev.start ? nextDay(ev.date) : ev.date;
      venueEntry.slots.set(slotKey, {
        start: `${ev.date}T${ev.start}`,
        end: `${endDate}T${ev.end}`,
        requiredTeam: 0,
        events: [ev],
      });
    } else {
      // Même événement, billets éclatés : on garde l'horaire le plus large
      const startTime = existing.start.substring(11);
      const endTime = existing.end.substring(11);
      if (ev.start < startTime) existing.start = `${ev.date}T${ev.start}`;
      const endDate = ev.end > endTime ? (ev.end <= ev.start ? nextDay(ev.date) : ev.date) : existing.end.substring(0, 10);
      if (ev.end > endTime) existing.end = `${endDate}T${ev.end}`;
      existing.events.push(ev);
    }
  }

  // Calcul de l'effectif, une fois les fusions terminées
  const places: PlaceDraft[] = [];
  for (const [key, entry] of byVenue) {
    const slots = Array.from(entry.slots.values())
      .map(slot => ({
        ...slot,
        requiredTeam: computeRequiredTeam(
          slot.events[0]?.ticket || '',
          slot.events[0]?.maxQty || '',
          sumTickets(slot.events.map(e => e.maxQty)),
        ),
      }))
      .sort((a, b) => a.start.localeCompare(b.start));

    places.push({ name: entry.name, key, slots, description: buildDescription({ name: entry.name, slots }) });
  }

  places.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return { places, rowsRead: events.length, rowsSkipped, warnings };
}

// ─────────────────────────────────────────────────────────────
// Description markdown
// ─────────────────────────────────────────────────────────────

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** `2026-10-23` → `23 oct. 2026` */
export function formatDateFr(date: string): string {
  const [y, m, d] = date.split('-');
  if (!y || !m || !d) return date;
  return `${+d} ${MONTHS_FR[+m - 1] || m} ${y}`;
}

/** Détail des événements du lieu, affiché dans la description du point (markdown). */
export function buildDescription(place: { name: string; slots: PlaceSlot[] }): string {
  const blocks = place.slots.map(slot => {
    const ev = slot.events[0];
    const lines: string[] = [];

    const heading = slot.events.map(e => e.title).join(' / ');
    lines.push(`### ${heading}`);

    const meta: string[] = [`📅 ${formatDateFr(slot.start.slice(0, 10))} · 🕗 ${slot.start.slice(11)} → ${slot.end.slice(11)}`];
    if (ev?.category) meta.push(`🏷️ ${ev.category}`);
    if (ev?.ticket) meta.push(`🎟️ ${ev.ticket}`);
    lines.push(meta.join('  \n'));

    if (ev?.description) lines.push('', ev.description);
    if (ev?.link) lines.push('', `[Plus d'informations](${ev.link})`);

    return lines.join('\n');
  });

  const count = `${place.slots.length} événement${place.slots.length > 1 ? 's' : ''} programmé${place.slots.length > 1 ? 's' : ''}`;
  return [`## ${place.name}`, `*${count}*`, ...blocks].join('\n\n');
}
